import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Text,
} from 'grommet';
import { Previous, Refresh } from 'grommet-icons';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  SubmissionLoader,
  AppButton,
  Spinning,
  SimpleModal,
} from '@Components/Control';
import {
  ProductFormDropdownSelect,
  ProductFormSingleTextInput,
} from '@Components/Partial/Product/NewSearch';
import { StyledFormTextArea } from '@Components/Partial/Product/NewSearch/StyledProductFormComponent.jsx';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateTimeLabel } from '@Helpers/util';

/* eslint-disable camelcase -- manual claim API items use snake_case (claim_type, asset_id, …) */
import {
  NoDataPlaceholder,
} from '@Components/Partial/Product/SharedComponents.jsx';
import {
  initiateAdminFetchManualClaimRequest,
  initiateAdminUpdateManualClaimRequest,
} from '@Actions';
import { ManualClaimStatusPill } from '@Components/Partial/Product/ContentId/ManualClaims/manualClaimUiHelpers.jsx';
import {
  ADMIN_EDITABLE_REQUEST_STATUSES as REQUEST_STATUS_OPTIONS,
  MANUAL_CLAIM_ITEM_STATUS_OPTIONS as ITEM_STATUS_OPTIONS,
  MANUAL_CLAIM_ITEM_STATUS_OPTIONS_BY_TYPE,
  MANUAL_CLAIM_REQUEST_STATUS,
  MANUAL_CLAIM_ITEM_STATUS,
  MANUAL_CLAIM_SUBMISSION_TYPE,
} from '@Components/Partial/Product/ContentId/ManualClaims/manualClaimConstants';

const formatStatusLabel = (value) => Case.title(String(value || '').replace(/_/g, ' '));

function badgeBg(isDarkMode, page, kind = 'neutral') {
  if (kind === 'primary') return isDarkMode ? page.inputBg : page.altBg;
  return isDarkMode ? page.altBg : page.inputBg;
}

function itemLineToFormFields(it) {
  if (!it) {
    return { status: undefined, youtubeClaimId: '', rejectionNote: '' };
  }
  const yt = it.youtube_claim_id ?? it.youtubeClaimId;
  const note = it.rejection_note ?? it.rejectionNote;
  return {
    status: it.status,
    youtubeClaimId: yt == null || yt === '' ? '' : String(yt),
    rejectionNote: note == null || note === '' ? '' : String(note),
  };
}

function buildFormState(detail) {
  if (!detail) {
    return { status: MANUAL_CLAIM_REQUEST_STATUS.CLAIM_REQUESTED, items: [] };
  }
  return {
    status: detail.status,
    items: (detail.items || []).map((it) => itemLineToFormFields(it)),
  };
}

function mergeDetailFromPatch(prev, patch) {
  if (!patch || !prev) return prev;
  const merged = { ...prev, ...patch };
  if (Array.isArray(patch.items)) {
    merged.items = patch.items;
  }
  return merged;
}

const AdminManualClaimDetailPage = ({
  small,
  location,
  fetchDetail,
  updateRequest,
  submitLoading,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const [detail, setDetail] = React.useState(null);
  const [form, setForm] = React.useState(() => buildFormState(null));
  const [loading, setLoading] = React.useState(true);
  const [showCompletePopup, setShowCompletePopup] = React.useState(false);
  /** Sync guard: stale async PATCHes cannot read React state reliably; ref is immediate. */
  const isCompletedRef = React.useRef(false);
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  React.useEffect(() => {
    isCompletedRef.current = false;
  }, [id]);

  /** Refetch full request (GET). Use after status-only PATCHes so item fields match the server. */
  const reloadDetail = React.useCallback(async (showPageLoading = true) => {
    if (!id) return;
    if (showPageLoading) setLoading(true);
    try {
      const data = await fetchDetail(id);
      if (data) {
        setDetail(data);
        setForm(buildFormState(data));
      } else {
        setDetail(null);
      }
    } finally {
      if (showPageLoading) setLoading(false);
    }
  }, [fetchDetail, id]);

  React.useEffect(() => {
    reloadDetail(true);
  }, [reloadDetail]);

  const updateItem = (idx, patch) => {
    setForm((prev) => {
      const nextItems = [...prev.items];
      nextItems[idx] = { ...nextItems[idx], ...patch };
      return { ...prev, items: nextItems };
    });
  };

  const applyFullSaveResponse = React.useCallback((patch) => {
    if (!patch) return;
    let normalized = { ...patch };
    if (
      isCompletedRef.current
      && normalized.status !== MANUAL_CLAIM_REQUEST_STATUS.COMPLETED
    ) {
      normalized = { ...normalized, status: MANUAL_CLAIM_REQUEST_STATUS.COMPLETED };
    }
    setDetail((prev) => mergeDetailFromPatch(prev, normalized));
    // Keep form as-is after save — admin input is authoritative; avoid slow/stale PATCH echo.
  }, []);

  const patchStatusQuiet = React.useCallback(async (body) => {
    if (!id) return;
    const updated = await updateRequest(id, body, { silent: true, quiet: true });
    if (!updated) return;
    if (isCompletedRef.current) return;

    setDetail((prev) => {
      if (!prev) return mergeDetailFromPatch(prev, updated);
      // Admin explicitly PATCHed request status — always merge (fixes reopen after completed).
      if (body.status) {
        return mergeDetailFromPatch(prev, updated);
      }
      // Item-only PATCH: block stale auto-derived UNDER_REVIEW overwriting COMPLETED.
      if (prev.status === MANUAL_CLAIM_REQUEST_STATUS.COMPLETED) {
        return prev;
      }
      return mergeDetailFromPatch(prev, updated);
    });
    if (!body.status && updated.status) {
      setForm((f) => {
        if (f.status === MANUAL_CLAIM_REQUEST_STATUS.COMPLETED) return f;
        return { ...f, status: updated.status };
      });
    }
  }, [id, updateRequest]);

  const onSave = async () => {
    if (!id) return;
    /* eslint-disable camelcase -- API contract uses snake_case */
    const body = {
      status: form.status,
      items: form.items.map((it, index) => ({
        index,
        status: it.status,
        youtube_claim_id: it.youtubeClaimId.trim() || null,
        rejection_note: it.rejectionNote.trim() || null,
      })),
    };
    /* eslint-enable camelcase */
    const updated = await updateRequest(id, body);
    if (updated) applyFullSaveResponse(updated);
  };

  const items = detail?.items || [];

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manual claim status & review',
        subTitle: 'Content ID · QA',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !loading && (
          <Box width="fit-content" direction="row" gap="1rem">
            <AppButton
              overrideHover
              onClick={() => navigate(paths.adminManualClaims)}
              label="Go back"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={() => id && reloadDetail(true)}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={!id || submitLoading}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      <Box
        flex
        background={usePageBg}
        pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '2rem' }}
        gap="medium"
        width="100%"
      >
        {submitLoading && (
          <SubmissionLoader
            textColor={page.primaryText}
            loaderColor={page.highlight}
            message="Saving updates..."
            contentWidth="20rem"
          />
        )}
        <Box fill="horizontal" gap="medium" direction="column">
          {loading && (
            <Box align="center" justify="center" pad="large" flex>
              <Spinning size="large" color={page.highlight} />
            </Box>
          )}

          {!loading && !detail && (
            <NoDataPlaceholder
              loading={false}
              width="100%"
              label="Manual claim request not found."
              textColor={page.primaryText}
              loaderColor={page.highlight}
            />
          )}

          {!loading && detail && (
            <>
              <Box
                direction={small ? 'column' : 'row'}
                gap="medium"
                width="100%"
                align={small ? 'start' : 'stretch'}
              >
                <Box
                  flex="grow"
                  direction="column"
                  gap="0.75rem"
                  pad="1rem"
                  background={useSectionBg}
                  round="var(--app-radius)"
                  width={small ? '100%' : { max: '50%' }}
                  style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
                >
                  <Text size="1.25rem" weight={600} color={page.primaryText}>
                    Request info
                  </Text>

                  <Box direction="column" gap="0.5rem">
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text weight={500} color={page.primaryText}>Request ID:</Text>
                      <Text
                        color={page.primaryText}
                        size="1rem"
                        style={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail.id}
                      </Text>
                    </Box>
                    <Text color={page.primaryText}>
                      <strong>Company:</strong>
                      {' '}
                      {detail.companyId || '—'}
                      {' '}
                      ·
                      {' '}
                      <strong>User:</strong>
                      {' '}
                      {detail.userId || '—'}
                    </Text>
                    <Box direction="row" align="center" gap="0.5rem" wrap>
                      <Text weight={500} color={page.primaryText}>Status:</Text>
                      <ManualClaimStatusPill status={form.status} page={page} />
                    </Box>
                    <Box direction="row" gap="0.5rem" align="center" wrap>
                      <Text weight={500} color={page.primaryText}>Submitted:</Text>
                      <Text color={page.primaryText} size="1rem">
                        {detail.createdAt ? buildDateTimeLabel(detail.createdAt) : '—'}
                      </Text>
                    </Box>
                    <Text color={page.primaryText}>
                      <strong>Type:</strong>
                      {' '}
                      {detail.submissionType === MANUAL_CLAIM_SUBMISSION_TYPE.CSV_UPLOAD ? 'CSV upload' : 'URL input'}
                      {detail.csvFilename ? ` (${detail.csvFilename})` : ''}
                    </Text>
                  </Box>
                </Box>

                <Box
                  flex="grow"
                  direction="column"
                  gap="0.75rem"
                  pad="1rem"
                  background={useSectionBg}
                  round="var(--app-radius)"
                  width={small ? '100%' : { max: '50%' }}
                  style={{ minWidth: 0, boxShadow: 'var(--elevation)' }}
                >
                  <Text size="1.25rem" weight={600} color={page.primaryText}>
                    Review & update
                  </Text>

                  <Box direction="column" gap="0.75rem">
                    <Box gap="xsmall">
                      <Text size="0.9rem" color={page.primaryText} weight={600}>
                        Request status
                      </Text>
                      <ProductFormDropdownSelect
                        simpleOptions={REQUEST_STATUS_OPTIONS}
                        selectable
                        value={form.status}
                        label={formatStatusLabel(form.status)}
                        handleFormValues={(val) => {
                          isCompletedRef.current = val === MANUAL_CLAIM_REQUEST_STATUS.COMPLETED;
                          setForm((f) => ({ ...f, status: val }));
                          patchStatusQuiet({ status: val });
                        }}
                      />
                    </Box>

                  </Box>
                </Box>
              </Box>

              <Box gap="small" width="100%" pad={{ top: 'medium' }}>
                <Box direction="row" justify="between" align="center" wrap gap="small">
                  <Text size="1.1rem" weight={600} color={page.primaryText}>
                    Claims
                    {' '}
                    (
                    {items.length}
                    )
                  </Text>
                  {items.length > 0 && (
                    <Box direction="row" align="center" gap="0.75rem" wrap justify="end">
                      {items.length > 1 && (
                        <Box direction="row" align="center" gap="0.4rem">
                          <Text size="0.85rem" color={page.secondaryText}>Apply to all:</Text>
                          <Box width="10rem">
                            <ProductFormDropdownSelect
                              simpleOptions={ITEM_STATUS_OPTIONS}
                              selectable
                              value=""
                              label="Select status"
                              handleFormValues={(val) => {
                                if (!val) return;
                                const nextItems = form.items.map((it) => ({ ...it, status: val }));
                                setForm((prev) => ({
                                  ...prev,
                                  items: nextItems,
                                }));
                                patchStatusQuiet({
                                  items: nextItems.map((it, index) => ({
                                    index,
                                    status: it.status,
                                  })),
                                });
                                if (
                                  form.status !== MANUAL_CLAIM_REQUEST_STATUS.COMPLETED
                                  && nextItems.every(
                                    (it) => it.status !== MANUAL_CLAIM_ITEM_STATUS.PENDING,
                                  )
                                ) {
                                  setShowCompletePopup(true);
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      <AppButton
                        overrideHover
                        level="listRequestNew"
                        label="Save notes & IDs"
                        bgColor={button.primaryBg}
                        color={button.primaryText}
                        onClick={onSave}
                        disabled={submitLoading}
                      />
                    </Box>
                  )}
                </Box>

                {items.length === 0 ? (
                  <NoDataPlaceholder
                    loading={false}
                    width="100%"
                    label="No line items found for this request."
                    textColor={page.primaryText}
                    loaderColor={page.highlight}
                  />
                ) : (
                  <Box gap="medium" width="100%">
                    {items.map((item, idx) => (
                      <Box
                        key={[
                          detail.id,
                          item.asset_id,
                          item.infringing_url,
                          item.original_link,
                          item.claim_action_type ?? item.claim_type,
                          item.match_type,
                          item.match_timeframe,
                          item.channel_link,
                          item.youtube_claim_id,
                          item.rejection_note,
                        ].join('||')}
                        background={page.cardBg}
                        border={{ color: page.tableBorder, size: '1px' }}
                        round="var(--app-radius)"
                        pad="1rem"
                        gap="small"
                        style={{ boxShadow: 'var(--elevation)' }}
                        width="100%"
                      >
                        <Text size="0.85rem" color={page.secondaryText} weight={600}>
                          Claim
                          {' '}
                          {idx + 1}
                        </Text>
                        <Box direction={small ? 'column' : 'row'} justify="between" align={small ? 'start' : 'center'} gap="small" wrap>
                          <Box direction="row" gap="small" wrap align="center" style={{ minWidth: 0 }}>
                            <Box
                              background={badgeBg(isDarkMode, page, 'primary')}
                              border={{ color: page.tableBorder, size: '1px' }}
                              round="0.6rem"
                              pad={{ vertical: '0.35rem', horizontal: '0.6rem' }}
                            >
                              <Text size="0.9rem" color={page.primaryText} weight={600} style={{ wordBreak: 'break-all' }}>
                                Asset ID:
                                {' '}
                                {item.asset_id || '—'}
                              </Text>
                            </Box>
                            <Box
                              background={badgeBg(isDarkMode, page)}
                              border={{ color: page.tableBorder, size: '1px' }}
                              round="0.6rem"
                              pad={{ vertical: '0.35rem', horizontal: '0.6rem' }}
                            >
                              <Text size="0.9rem" color={page.primaryText} weight={600}>
                                Claim type:
                                {' '}
                                {(item.claim_action_type ?? item.claim_type) || '—'}
                              </Text>
                            </Box>
                            <Box
                              background={badgeBg(isDarkMode, page)}
                              border={{ color: page.tableBorder, size: '1px' }}
                              round="0.6rem"
                              pad={{ vertical: '0.35rem', horizontal: '0.6rem' }}
                            >
                              <Text size="0.9rem" color={page.primaryText} weight={600} style={{ wordBreak: 'break-word' }}>
                                Match Type:
                                {' '}
                                {item.match_type || '—'}
                              </Text>
                            </Box>
                          </Box>

                          <Box gap="xsmall" width={small ? '100%' : { max: '16rem' }}>
                            <Text size="0.8rem" color={page.secondaryText}>Claim status</Text>
                            <ProductFormDropdownSelect
                              simpleOptions={
                                MANUAL_CLAIM_ITEM_STATUS_OPTIONS_BY_TYPE[
                                  item.claim_action_type
                                ] || ITEM_STATUS_OPTIONS
                              }
                              selectable
                              value={form.items[idx]?.status}
                              label={formatStatusLabel(form.items[idx]?.status)}
                              handleFormValues={(val) => {
                                updateItem(idx, { status: val });
                                patchStatusQuiet({
                                  items: [{ index: idx, status: val }],
                                });
                                const nextItems = form.items.map((it, i) => (
                                  i === idx ? { ...it, status: val } : it
                                ));
                                if (
                                  form.status !== MANUAL_CLAIM_REQUEST_STATUS.COMPLETED
                                  && nextItems.every(
                                    (it) => it.status !== MANUAL_CLAIM_ITEM_STATUS.PENDING,
                                  )
                                ) {
                                  setShowCompletePopup(true);
                                }
                              }}
                            />
                          </Box>
                        </Box>

                        <Box gap="0.3rem" width="100%">
                          <Text size="0.95rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
                            <Text size="0.95rem" color={page.secondaryText} weight={600}>Infringing URL:</Text>
                            {' '}
                            {item.infringing_url || '—'}
                          </Text>
                          <Text size="0.95rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
                            <Text size="0.95rem" color={page.secondaryText} weight={600}>Original link:</Text>
                            {' '}
                            {item.original_link || '—'}
                          </Text>
                          <Text size="0.95rem" color={page.tableText} style={{ wordBreak: 'break-word' }}>
                            <Text size="0.95rem" color={page.secondaryText} weight={600}>Match Time Frame:</Text>
                            {' '}
                            {item.match_timeframe || '—'}
                          </Text>
                          <Text size="0.95rem" color={page.tableText} style={{ wordBreak: 'break-all' }}>
                            <Text size="0.95rem" color={page.secondaryText} weight={600}>Channel:</Text>
                            {' '}
                            {item.channel_link || '—'}
                          </Text>
                        </Box>

                        <Box
                          direction={small ? 'column' : 'row'}
                          gap="medium"
                          wrap
                          background={useSectionBg}
                          round="0.5rem"
                          pad="0.75rem"
                          border={{ color: page.tableBorder, size: '1px' }}
                        >
                          <Box gap="xsmall" flex={small ? false : { grow: 1, shrink: 1 }} basis={small ? undefined : '18rem'} style={{ minWidth: 0 }}>
                            <Text size="0.8rem" color={page.secondaryText}>YouTube claim ID (optional)</Text>
                            <ProductFormSingleTextInput
                              focusHighlight={page.focusHighlight}
                              small={small}
                              value={form.items[idx]?.youtubeClaimId ?? ''}
                              handleFormValues={(val) => updateItem(idx, { youtubeClaimId: val })}
                              placeholder="Paste after creating claim in YouTube"
                            />
                          </Box>
                          <Box gap="xsmall" flex={small ? false : { grow: 2, shrink: 1 }} basis={small ? undefined : '26rem'} style={{ minWidth: 0 }}>
                            <Text size="0.8rem" color={page.secondaryText}>Note (optional)</Text>
                            <StyledFormTextArea
                              fill
                              highlight={page.focusHighlight}
                              resize="vertical"
                              rows={2}
                              value={form.items[idx]?.rejectionNote ?? ''}
                              onChange={(e) => updateItem(idx, { rejectionNote: e.target.value })}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box
                direction={small ? 'column' : 'row'}
                gap="small"
                align={small ? 'stretch' : 'center'}
                justify={small ? 'start' : 'end'}
                wrap
                pad={{ top: '0.5rem' }}
              >
                <AppButton
                  overrideHover
                  level="listRequestNew"
                  bgColor={isDarkMode ? page.inputBg : page.cardBg}
                  color={page.primaryText}
                  fontWeight={600}
                  label="Back to list"
                  onClick={() => navigate(paths.adminManualClaims)}
                  style={{
                    border: `1px solid ${page.tableBorder}`,
                    minWidth: '13rem',
                    boxSizing: 'border-box',
                  }}
                />
                <AppButton
                  overrideHover
                  level="listRequestNew"
                  label="Save notes & IDs"
                  bgColor={button.primaryBg}
                  color={button.primaryText}
                  onClick={onSave}
                  disabled={submitLoading}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      {showCompletePopup && (
        <SimpleModal
          small={small}
          modalTitle="All claims reviewed"
          toggleModal={() => setShowCompletePopup(false)}
          highlightColor={page.highlight}
          hideLowerSection
          noClickOutside
          desktopMinWidth="32rem"
        >
          <Box gap="medium" pad={{ bottom: '1.5rem' }}>
            <Text color={page.primaryText}>
              All claims have been actioned. Would you like to mark this request as
              {' '}
              <strong>Completed</strong>
              ?
            </Text>
            <Box direction="row" gap="small" justify="end">
              <AppButton
                overrideHover
                level="listRequestNew"
                label="Not yet"
                bgColor={isDarkMode ? page.inputBg : page.cardBg}
                color={page.primaryText}
                onClick={() => setShowCompletePopup(false)}
                style={{ border: `1px solid ${page.tableBorder}` }}
              />
              <AppButton
                overrideHover
                level="listRequestNew"
                label="Yes, mark as completed"
                bgColor={button.primaryBg}
                color={button.primaryText}
                onClick={async () => {
                  setShowCompletePopup(false);
                  isCompletedRef.current = true;
                  const updated = await updateRequest(id, {
                    status: MANUAL_CLAIM_REQUEST_STATUS.COMPLETED,
                  });
                  if (updated) {
                    const nextStatus = updated.status === MANUAL_CLAIM_REQUEST_STATUS.COMPLETED
                      ? updated.status
                      : MANUAL_CLAIM_REQUEST_STATUS.COMPLETED;
                    // Status only — keep drafts until Save notes & IDs or Refresh.
                    setDetail((prev) => (prev ? { ...prev, status: nextStatus } : prev));
                    setForm((f) => ({ ...f, status: nextStatus }));
                  }
                }}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchDetail: initiateAdminFetchManualClaimRequest,
      updateRequest: initiateAdminUpdateManualClaimRequest,
    },
    dispatch,
  );
}

AdminManualClaimDetailPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  fetchDetail: PropTypes.func.isRequired,
  updateRequest: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminManualClaimDetailPage);
